import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Calendar, ExternalLink, MapPin, MessageCircle } from 'lucide-react';
import api from '../../services/api.js';
import { formatMoneyCOP } from '../../utils/formatMoney.js';
import { getVacacionWhatsAppHref } from '../../utils/vacacionWhatsApp.js';
import { getVacacionImagePaths } from '../../utils/vacacionImages.js';
import publicCta from '../../content/publicCta.json';
import { vacacionesReglamento } from '../../content/vacacionesReglamento.js';
import BrandLoading from '../../components/ui/BrandLoading.jsx';
import PremiumCard from '../../components/ui/PremiumCard.jsx';
import PublicPageHero from '../../components/public/PublicPageHero.jsx';
import VacacionImageGallery from '../../components/public/VacacionImageGallery.jsx';
import VacacionMapEmbed from '../../components/public/VacacionMapEmbed.jsx';
import VacacionTarifasSede from '../../components/public/VacacionTarifasSede.jsx';
import { getTarifasForSedeId } from '../../constants/vacacionesTarifas.js';
import { usePageSeo } from '../../hooks/usePageSeo.js';
import { assetUrl } from '../../utils/assetUrl.js';

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' });
}

export default function SedesVacacionalesDetailPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setNotFound(false);
      setError(null);
      try {
        const { data: res } = await api.get(`/v1/public/vacaciones/${id}`);
        if (cancelled) return;
        setData(res?.data ?? null);
      } catch (e) {
        if (cancelled) return;
        if (e.status === 404) setNotFound(true);
        else setError(e.message || 'No se pudo cargar la sede vacacional');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (id) load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const whatsappHref = data ? getVacacionWhatsAppHref(data) : null;
  const photoCount = data ? getVacacionImagePaths(data).length : 0;
  const tarifasSede = data ? getTarifasForSedeId(data.id) : null;
  const precioDesde =
    tarifasSede && 'filas' in tarifasSede
      ? Math.min(...tarifasSede.filas.flatMap((f) => f.dias))
      : tarifasSede?.casas?.[0]?.filas?.[0]?.valor;

  const heroTitle = loading
    ? 'Cargando…'
    : data?.titulo || (notFound ? 'Sede no encontrada' : 'Sede vacacional');

  usePageSeo(
    loading || notFound
      ? { title: notFound ? 'Sede no encontrada' : 'Sede vacacional' }
      : data
        ? {
            title: data.titulo,
            description: data.descripcion?.slice(0, 320),
            image: data.imagen ? assetUrl(data.imagen) : undefined,
            type: 'article',
          }
        : null
  );

  const heroDescription = loading
    ? ''
    : data?.descripcion?.slice(0, 160) ||
      (notFound
        ? 'Esta sede no está disponible o ya no se publica.'
        : error || '');

  return (
    <div className="pb-20">
      <PublicPageHero
        backTo="/sedes-vacacionales"
        backLabel="Volver a sedes vacacionales"
        eyebrow={data?.ubicacion || 'Sedes vacacionales'}
        title={heroTitle}
        description={heroDescription}
      />

      <section className="border-b border-slate-200 bg-white py-14 lg:py-20">
        <div className="mx-auto max-w-6xl px-6 lg:px-8">
          {loading && (
            <BrandLoading variant="inline" tone="light" label="Cargando sede…" className="min-h-[32rem]" />
          )}

          {!loading && error && !data && <p className="text-slate-600">{error}</p>}

          {!loading && data && (
            <article className="space-y-8">
              <PremiumCard variant="light" className="overflow-hidden !p-6 sm:!p-8">
                <div className="grid gap-8 lg:grid-cols-12 lg:gap-10 lg:items-start">
                  <div className="min-w-0 lg:col-span-5">
                    <VacacionImageGallery sede={data} />
                    {photoCount > 0 ? (
                      <p className="mt-3 text-center text-xs text-slate-500">
                        {photoCount} {photoCount === 1 ? 'foto' : 'fotos'} — selecciona una miniatura para ampliarla
                      </p>
                    ) : null}
                  </div>

                  <div className="min-w-0 space-y-6 lg:col-span-7">
                    <div className="flex flex-wrap gap-3 text-sm text-slate-700">
                      {data.ubicacion ? (
                        <span className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                          <MapPin className="h-4 w-4 shrink-0 text-accent-lime" aria-hidden />
                          {data.ubicacion}
                        </span>
                      ) : null}
                      <span className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                        <Calendar className="h-4 w-4 shrink-0 text-accent-lime" aria-hidden />
                        Vigencia: {formatDate(data.fecha_inicio)} — {formatDate(data.fecha_fin)}
                      </span>
                    </div>

                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Desde (por noche)</p>
                      <p className="font-display text-3xl font-bold text-accent-lime">
                        {precioDesde ? formatMoneyCOP(precioDesde) : Number(data.precio) > 0 ? formatMoneyCOP(data.precio) : 'Consultar'}
                      </p>
                      <p className="mt-1 text-sm text-slate-500">
                        Tarifa de referencia según tabla vigente y día de la semana.
                      </p>
                    </div>

                    {tarifasSede ? (
                      <VacacionTarifasSede
                        sedeId={data.id}
                        className="rounded-xl border border-slate-200 bg-slate-50/80 p-5 ring-1 ring-slate-100"
                      />
                    ) : null}

                    {data.descripcion ? (
                      <p className="text-justify text-base leading-relaxed text-slate-600">{data.descripcion}</p>
                    ) : (
                      <p className="text-slate-500">No hay descripción adicional publicada.</p>
                    )}

                    <div className="flex flex-wrap gap-3 border-t border-slate-200 pt-6">
                      {whatsappHref ? (
                        <a
                          href={whatsappHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 rounded-lg border border-[#25D366]/50 bg-[#25D366] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#20bd5a]"
                        >
                          <MessageCircle className="h-4 w-4" aria-hidden />
                          {publicCta.vacacionesCardLinkLabel}
                          <ExternalLink className="h-4 w-4" aria-hidden />
                        </a>
                      ) : null}
                      <Link
                        to="/sedes-vacacionales#reglamento"
                        className="inline-flex items-center gap-2 rounded-lg border border-[#1acb12]/40 bg-white px-5 py-2.5 text-sm font-semibold text-accent-lime transition hover:border-[#1acb12]/70 hover:text-[#0d2b7f]"
                      >
                        Ver reglamento y tarifas
                      </Link>
                      <a
                        href={vacacionesReglamento.meta.pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-5 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
                      >
                        Reglamento PDF
                        <ExternalLink className="h-4 w-4" aria-hidden />
                      </a>
                    </div>
                  </div>
                </div>
              </PremiumCard>

              <VacacionMapEmbed
                mapaEmbed={data.mapa_embed}
                ubicacionLabel={data.ubicacion}
                className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm lg:p-8"
              />

              <p className="text-center text-sm text-slate-500">
                Reserva con el formato oficial de la cooperativa.{' '}
                <Link to="/contacto" className="font-semibold text-accent-lime hover:text-[#0d2b7f]">
                  Contáctanos
                </Link>{' '}
                si necesitas orientación.
              </p>
            </article>
          )}
        </div>
      </section>
    </div>
  );
}
